import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { MessageResponse } from '@voll-intelligence/domain';

import { conversationRepository } from '@web/data/repositories';
import {
  dedupById,
  messagesToThread,
  type ThreadMessage,
} from '@web/data/repositories/conversation/messages_mapper';

const PAGE_SIZE = 50;

type Page = { data: MessageResponse[]; nextCursor: string | null };
type Status = 'idle' | 'loading' | 'failed';
interface OlderState {
  conversationId: string;
  /** The first page's cursor this chain was started from. */
  firstCursor: string | null;
  pages: Page[];
  status: Status;
}

function freshState(conversationId: string, firstCursor: string | null): OlderState {
  return { conversationId, firstCursor, pages: [], status: 'idle' };
}

export interface OlderMessages {
  /** Everything before the thread's first page, mapped for the thread. */
  messages: ThreadMessage[];
  /** There is an older page to ask for. */
  hasMore: boolean;
  loading: boolean;
  failed: boolean;
  loadOlder: () => void;
}

/**
 * Walks the history back past the thread's first page (`order=desc`, 50 per
 * page). The thread query stays the owner of the newest page and of the SSE
 * merge; this hook only follows the cursor that page returned, then the cursor
 * of each page it loads. A different conversation starts over, and so does a
 * refetched first page (its cursor changes): the cursor is a keyset anchored on
 * the page's last message, so a chain started from the old first page would
 * skip whatever moved between the two. Both are derived from the inputs, no
 * effect needed.
 */
export function useOlderMessages(
  conversationId: string,
  firstCursor: string | null,
): OlderMessages {
  const [state, setState] = useState<OlderState>(freshState(conversationId, firstCursor));
  const isCurrent = (s: OlderState) =>
    s.conversationId === conversationId && s.firstCursor === firstCursor;
  const own = isCurrent(state) ? state : freshState(conversationId, firstCursor);
  const cursor = own.pages.length === 0 ? firstCursor : (own.pages.at(-1)?.nextCursor ?? null);

  function loadOlder(): void {
    if (cursor === null || own.status === 'loading') return;
    setState({ ...own, status: 'loading' });
    const settle = (next: (prev: OlderState) => OlderState) => {
      setState((prev) => (isCurrent(prev) ? next(prev) : prev));
    };
    conversationRepository
      .loadMessages(conversationId, { order: 'desc', limit: PAGE_SIZE, cursor })
      .then((page) => {
        settle((prev) => ({ ...prev, pages: [...prev.pages, page], status: 'idle' }));
      })
      .catch(() => {
        settle((prev) => ({ ...prev, status: 'failed' }));
      });
  }

  return {
    messages: messagesToThread(own.pages.flatMap((p) => p.data)),
    hasMore: cursor !== null,
    loading: own.status === 'loading',
    failed: own.status === 'failed',
    loadOlder,
  };
}

/** The thread's messages with the older pages in front, deduped and in time order. */
export function withOlder(older: ThreadMessage[], newest: ThreadMessage[]): ThreadMessage[] {
  if (older.length === 0) return newest;
  return dedupById([...older, ...newest]);
}

interface ScrollAnchor {
  row: HTMLElement;
  /** How far below the scroller's top edge the row sat. */
  offset: number;
}

function offsetWithin(row: HTMLElement, scroller: HTMLElement): number {
  return row.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
}

/**
 * Keeps the message the operator was reading in place when older ones land on
 * top: the returned `beforePrepend()` records the first message row (the oldest
 * one shown, next to the button) and where it sits in the scroller, and once the
 * older count grows the scroll is moved until that row is back at the same
 * place. Anchored on a row, not on the distance from the bottom, so a live
 * message appended while the page was loading does not shift the view.
 */
export function useKeepScrollOnPrepend(
  scrollRef: RefObject<HTMLDivElement | null>,
  olderCount: number,
): () => void {
  const anchorRef = useRef<ScrollAnchor | null>(null);
  const countRef = useRef(olderCount);
  useLayoutEffect(() => {
    // Only a change in the older count is a prepend to restore the anchor for.
    const prepended = countRef.current !== olderCount;
    countRef.current = olderCount;
    const el = scrollRef.current;
    const anchor = anchorRef.current;
    if (!prepended || el === null || anchor === null) return;
    anchorRef.current = null;
    if (anchor.row.isConnected) el.scrollTop += offsetWithin(anchor.row, el) - anchor.offset;
  }, [scrollRef, olderCount]);
  return useCallback(() => {
    const el = scrollRef.current;
    const row = el?.querySelector<HTMLElement>('[data-message-id]');
    anchorRef.current = el && row ? { row, offset: offsetWithin(row, el) } : null;
  }, [scrollRef]);
}
